/**
 * The contact channels as recognisable marks, drawn for a tinted tile.
 *
 * Three treatments were rendered on the real header and compared (see
 * `ALT-*.png`): saturated discs, flat glyphs, tinted tiles, labelled pills. The
 * discs shouted — three fully saturated circles were the loudest thing in an
 * otherwise quiet, white header. A pale tile with a dark glyph keeps the colour
 * coding, so a couple still finds its channel at a glance, without the header
 * turning into a social bar.
 *
 * The colours are not free choices, they are what the measurements allow. The
 * app-icon colours (#25D366, #229ED9) reach only 1.79:1 and 2.67:1 against
 * their own tile — invisible. Both brands publish a darker colour for exactly
 * this case, and those pass: WhatsApp #128C7E at 3.73:1, Telegram #0088CC at
 * 3.45:1. The tiles themselves are near-white on purpose (1.11–1.15:1 against
 * the header); they carry no information, only grouping.
 *
 * Trademark note: both marks link to our own account on that service, in the
 * brands' own published colours and undistorted. That is nominative use. They
 * must not be recoloured into the house palette or set on a background that
 * implies a partnership.
 */

/** WhatsApp's published dark green — the app-icon green is unreadable here. */
const WHATSAPP_GREEN = "#128C7E";
/** Telegram's classic blue, likewise the darker of the two published values. */
const TELEGRAM_BLUE = "#0088CC";

const WHATSAPP_GLYPH =
  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z";

const TELEGRAM_GLYPH =
  "M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z";

/**
 * `mono` draws the mark in the current text colour instead of the fixed
 * colour, so the caller picks it: the header sets the WhatsApp mark white on
 * a round #25D366 badge and the Telegram disc in its app-icon blue #229ED9 —
 * the round app-icon versions of both brands.
 */
type IconProps = { className?: string; mono?: boolean };

export function WhatsAppIcon({ className, mono }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill={mono ? "currentColor" : WHATSAPP_GREEN} aria-hidden>
      <path d={WHATSAPP_GLYPH} />
    </svg>
  );
}

export function TelegramIcon({ className, mono }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill={mono ? "currentColor" : TELEGRAM_BLUE} aria-hidden>
      <path d={TELEGRAM_GLYPH} />
    </svg>
  );
}

/**
 * E-mail has no brand, so it takes the house red — the same red as the flag in
 * the wordmark — and the same tile, so the three read as one row rather than
 * two apps and an afterthought.
 */
export function MailDiscIcon({ className, mono }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke={mono ? "currentColor" : "var(--color-coral-600)"}
      strokeWidth="1.85"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2.4" y="4.8" width="19.2" height="14.4" rx="2.2" />
      <path d="m3.5 6.6 8.5 5.9 8.5-5.9" />
    </svg>
  );
}

/** Tile background per channel, kept next to the glyph colours they pair with. */
export const CHANNEL_TILE = {
  whatsapp: "bg-[#E9F6F3]",
  telegram: "bg-[#E7F3FB]",
  mail: "bg-[#FBEBEE]",
} as const;
